import React from 'react'
import { useState, useEffect, useCallback } from 'react'
import { Formik as BaseFormik, FormikHelpers } from 'formik'
import Ajv from 'ajv'
import addFormats from 'ajv-formats'
import AjvKeywords from 'ajv-keywords'
import cleanDeep from 'clean-deep'
import { JsonPointer as ptr } from 'json-ptr'

import { afterDateKeyword } from './afterDate.keyword'
import { issueNumberFormat } from './issueNumber.format'
import { dateTimeFormat } from './date-time.format'

import './base-form.scss'

// export for testing
export const ajvErrorsToFormikErrors = (schema: any, ajvErrors: any, subobj: string | undefined) => {
    const data = {}

    if (ajvErrors === null) {
        return data
    }

    ajvErrors.forEach((ajvError: any) => {
        if (ajvError.keyword === 'if' &&
            (ajvError.params.failingKeyword === 'then' || ajvError.params.failingKeyword === 'else')) {
            return;
        }

        // ****** IMPORTANT ******
        // mock the i18next t function so i18next-scanner would pick the
        // dynamic keys used for the validation errors
        // actual translation would be done in the element that displays the
        // error to change the messages language without rerunning the
        // validation
        const t = (x: any) => x

        let path = ajvError.instancePath;

        if (ajvError.params.missingProperty) {
            path += `/${ajvError.params.missingProperty}`;
        } else if (ajvError.instancePath) {
            path = ajvError.instancePath.replace('.', '/')
        } else {
            path = '/errors'
        }

        if (subobj) {
            path = `/${subobj}${path}`
        }

        let msg = {}
        const params = ajvError.params


        switch(ajvError.keyword) {
            case 'required':
                // msg = 'Field is required'
                msg = { key: t('global:error.validation.required') }
                break
            case 'enum':
                msg = { key: t('global:error.validation.enum'), options: { allowedValues: params.allowedValues.join('/') } }
                // msg = `Field's value must be one of ${params.allowedValues.join('/')}`
                break
            case 'minLength':
                msg = { key: t('global:error.validation.minLength'), options: { limit: params.limit } }
                // msg = `Field's value should not be less than ${params.limit} characters`
                break
            case 'maxLength':
                msg = { key: t('global:error.validation.maxLength'), options: { limit: params.limig } }
                // msg = `Field's value should not be more than ${params.limit} characters`
                break
            case 'format':
                msg = { key: t('global:error.validation.format'), options: { format: params.format } }
                // msg = `Field's value is not a valid ${params.format}`
                break
            case 'formatMinimum': {
                const format = String(ptr.get(schema, ajvError.schemaPath.replace('formatMinimum', 'format')))
                msg = { key: t('global:error.validation.formatMinimum'), options: { format, limit: params.limit, context: params.exclusive ? 'exclusive' : '' } }
                // msg = `Field's value should be ${params.exclusive ? 'greater than' : 'greater or equal to'} ${formatDateTime(format, params.limit)}`
                break;
            }
            case 'afterDate': {
                const format = String(ptr.get(schema, ajvError.schemaPath.replace('afterDate', 'format')))
                msg = { key: t('global:error.validation.afterDate'), options: { distance: params.distance, units: params.units, limit: params.limit, format } }
                // msg = `Field's value should be at most ${params.distance} ${params.units} after ${formatDateTime(format, params.limit)} and not before`
                break
            }
            default:
                msg = { key: t('global:error.validation.undefined'), options: { error: ajvError.message } }
                //msg = ajvError.message
        }

        ptr.set(data, path, msg, true);
    });

    return data;
}

export const BaseForm = ({ ajvOptions, jsonSchema, additionalSchemas, subobjects, customValidation, submitValidation, onSubmit, ...props }: any) => {
    const [ajv, setAjv] = useState<Ajv|null>(null)
    const [validators, setValidators] = useState<any>({})

    useEffect(() => {
        const tempAjv = new Ajv({
            allErrors: true,
            $data: true,
            ...ajvOptions
        })
        const _validators: any = {}

        // ajv-formats plugin
        addFormats(tempAjv)
        // ajv-keywords plugin
        AjvKeywords(tempAjv)

        // custom formats
        issueNumberFormat(tempAjv)

        // custom keywords
        afterDateKeyword(tempAjv)

        // custom datetime format to support datetimes without timezone
        dateTimeFormat(tempAjv)

        // add schema
        _validators['schema'] = tempAjv.compile(jsonSchema || {})
        // tempAjv.addSchema(jsonSchema || {}, 'schema')
        if (additionalSchemas) {
            Object.entries<any>(additionalSchemas).forEach(([name, schema]) => {
                _validators[name] = tempAjv.compile(schema || {})
                // tempAjv.addSchema(schema, name)
            })
        }

        setValidators(_validators)
        setAjv(tempAjv)
    }, [ajvOptions, jsonSchema, additionalSchemas])

    const handleValidate = useCallback(async (values: any) => {
        if (!ajv) { return }

        const newValues = cleanDeep(values, {
            emptyArrays: false,
            emptyObjects: false,
            emptyStrings: true,
            nullValues: true,
            undefinedValues: false
        });

        let errors = {}
        if (subobjects) {
            Object.entries<string>(subobjects).forEach(([name, schema]) => {
                validators[schema](newValues[name])
                errors = { ...errors, ...ajvErrorsToFormikErrors(additionalSchemas[schema], validators[schema].errors, name) }
            })
        }
        validators['schema'](newValues)

        errors = { ...errors, ...ajvErrorsToFormikErrors(jsonSchema, validators['schema'].errors, undefined) }

        if (customValidation) {
            const customErrors = await customValidation(newValues, errors)
            errors = { ...errors, ...customErrors }
        }

        return errors
    }, [ajv, validators, jsonSchema, customValidation, subobjects, additionalSchemas])

    const handleSubmit = useCallback(async (values: any, formikBag: FormikHelpers<any>) => {
        let errors = null
        if (submitValidation) {
            errors = await submitValidation(values)
            if (Object.keys(errors).length) {
                formikBag.setErrors(errors)
                return  // if submitValidation has any errors abort
            }
            // todo: check level of errors and decide whether to abort or not??
        }

        onSubmit(values, formikBag)
    }, [submitValidation, onSubmit])

    if (!ajv) {
        return null
    }


    return (
        <BaseFormik {...props} onSubmit={handleSubmit} validate={handleValidate}>
            {props.children}
        </BaseFormik>
    )
}
